/**
 * RecipientInput — a To/Cc/Bcc field with contact autocomplete.
 *
 * The field holds a comma/semicolon-separated list of addresses; autocomplete
 * operates only on the token currently being typed (the text after the last
 * separator), so earlier recipients are left untouched. Suggestions merge the
 * Vault CRM (contact-book) with the "from your mail" harvested cache for the
 * sending account (people you've previously emailed), CRM first. The dropdown is
 * portaled through FloatingOverlay so it isn't clipped by the composer's scroll.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FloatingOverlay } from '@/components/ui/floating-overlay';
import { cn } from '@/lib/utils';
import {
  useAddressBook,
  useHarvestedEmailContacts,
  contactDisplayName,
} from '../hooks/useAddressBook';

interface RecipientInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Sending account — enables "previously emailed" (harvested) suggestions. */
  accountId?: string;
}

interface Suggestion {
  email: string;
  label: string;
}

/** The recipient token currently being edited = text after the last `,`/`;`. */
function currentTokenOf(value: string): { token: string; prefixEnd: number } {
  const sep = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'));
  return { token: value.slice(sep + 1).trim(), prefixEnd: sep + 1 };
}

export function RecipientInput({
  id,
  value,
  onChange,
  placeholder,
  accountId,
}: RecipientInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  // "Previously emailed" suggestions default to the sending account; toggle to
  // pull harvested contacts from EVERY account (deduped) so you can address a
  // person you know from a different mailbox.
  const [searchAll, setSearchAll] = useState(false);

  const { token, prefixEnd } = currentTokenOf(value);
  // Debounce the query term so we don't hit the API on every keystroke.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(token), 150);
    return () => clearTimeout(t);
  }, [token]);

  const querying = open && debounced.length >= 2;
  const { data: contacts } = useAddressBook(querying, debounced);
  const { data: harvested } = useHarvestedEmailContacts(
    searchAll ? undefined : accountId,
    querying,
    debounced,
  );

  const suggestions = useMemo<Suggestion[]>(() => {
    if (!querying) return [];
    const lc = debounced.toLowerCase();
    const seen = new Set<string>();
    const out: Suggestion[] = [];
    const push = (email: string | undefined, label: string) => {
      const addr = email?.trim();
      if (!addr) return;
      const key = addr.toLowerCase();
      if (seen.has(key)) return;
      // Already-entered addresses shouldn't be suggested again.
      if (value.toLowerCase().includes(key) && key !== lc) return;
      seen.add(key);
      out.push({ email: addr, label: label || addr });
    };
    // CRM first (curated), then harvested "previously emailed" addresses.
    for (const c of contacts ?? []) {
      const label = contactDisplayName(c);
      for (const e of c.emails) {
        push(e.value, label);
        if (out.length >= 8) return out;
      }
    }
    for (const h of harvested ?? []) {
      push(h.emailAddress, h.displayName ?? h.emailAddress);
      if (out.length >= 8) return out;
    }
    return out;
  }, [contacts, harvested, querying, debounced, value]);

  useEffect(() => setActiveIdx(0), [suggestions.length]);

  // Show when there are suggestions, OR when actively querying a single account
  // (so the "all accounts" toggle + empty state are reachable even with no local
  // match, letting the user widen the search).
  const showDropdown = open && (suggestions.length > 0 || (querying && !!accountId));

  const choose = (email: string) => {
    const prefix = value.slice(0, prefixEnd);
    const sep = prefix && !/\s$/.test(prefix) ? ' ' : '';
    onChange(`${prefix}${sep}${email}, `);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <>
      <Input
        id={id}
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!showDropdown) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
            // Enter and Tab both commit the highlighted suggestion. Tab only
            // intercepts when there's a suggestion to take, so it otherwise
            // keeps its normal move-to-next-field behavior.
            const pick = suggestions[activeIdx];
            if (pick) {
              e.preventDefault();
              choose(pick.email);
            }
          }
        }}
      />
      <FloatingOverlay
        open={showDropdown}
        onClose={() => setOpen(false)}
        anchorRef={inputRef}
        preferredPlacement="bottom"
        // No entrance animation: the dropdown re-opens as the query changes on
        // each keystroke, and the repeated zoom-in read as a flicker.
        animate={false}
      >
        <div
          className="overflow-hidden rounded-md border bg-popover text-sm shadow-md"
          style={{ width: inputRef.current?.offsetWidth }}
        >
          <ul className="max-h-56 overflow-auto py-1">
            {suggestions.length === 0 ? (
              <li className="px-2 py-1.5 text-xs text-muted-foreground">
                {searchAll ? 'No matching contacts.' : 'No matches in this account.'}
              </li>
            ) : (
              suggestions.map((s, i) => (
                <li key={s.email}>
                  <button
                    type="button"
                    className={`flex w-full flex-col items-start px-2 py-1.5 text-left ${
                      i === activeIdx ? 'bg-accent' : 'hover:bg-accent/50'
                    }`}
                    // mousedown (not click) so it fires before the input loses focus.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(s.email);
                    }}
                  >
                    <span className="truncate font-medium">{s.label}</span>
                    {s.label !== s.email && (
                      <span className="truncate text-xs text-muted-foreground">{s.email}</span>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
          {/* Widen the harvested-contact source to every account. Only offered
              when there's a specific sending account to broaden from. */}
          {accountId && (
            <button
              type="button"
              aria-pressed={searchAll}
              className="flex w-full items-center gap-1.5 border-t px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              // mousedown so it fires before the input blurs (keeps the dropdown open).
              onMouseDown={(e) => {
                e.preventDefault();
                setSearchAll((v) => !v);
              }}
            >
              <span
                className={cn(
                  'flex h-3.5 w-3.5 items-center justify-center rounded-sm border',
                  searchAll ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                )}
              >
                {searchAll && <Check className="h-2.5 w-2.5" />}
              </span>
              Include contacts from all accounts
            </button>
          )}
        </div>
      </FloatingOverlay>
    </>
  );
}
